import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * OtpCells, the segmented one-time-code cell row.
 *
 * CSS selector: `mobile/molecules.css` `.ds-otp`, with `.ds-otp__cell`
 * (+ `--active`).
 *
 * Doctrine: the travelling ring is accent and belongs to exactly one cell at a
 * time, because accent marks wayfinding state. The CSS applies it on `:focus`;
 * `activeIndex` lets a controlled parent mark the next empty cell without
 * stealing focus, for example after a resend.
 *
 * Auto-advance, backspace retreat and paste splitting live here, so callers
 * only manage the `values` array and `onChange`.
 *
 * NO `maxLength={1}`, deliberately, and this is the one detail worth knowing.
 * `maxlength` truncates BEFORE the input event fires, so a browser autofilling
 * the whole 6-digit code into the first cell silently drops five digits and the
 * user watches a single number appear. The single-digit rule is enforced here
 * instead, and ANY multi-character arrival is treated as a code to distribute,
 * which is what autofill, iOS "From Messages", Android keyboards and a fast
 * typist all produce. Paste is then just one more way for that to happen.
 *
 * `autocomplete="one-time-code"` goes on the FIRST cell only. On all six, the
 * browser offers the same code six times and fills whichever cell is focused.
 */
export const OtpCells = React.forwardRef(function OtpCells(
  { className, length = 6, values = [], onChange, activeIndex, disabled = false, ...rest },
  ref,
) {
  const inputRefs = React.useRef([]);

  /**
   * Writes `digits` across the cells starting at `from`, moves focus to the
   * last cell it touched, and reports up. Every path that can produce more
   * than one digit funnels through here: paste, autofill, and an input event
   * that arrives with a whole code in it.
   */
  function distribute(from, digits) {
    const clean = String(digits).replace(/[^0-9]/g, '').slice(0, length - from);
    if (!clean) return;
    const next = [...values];
    for (let i = 0; i < clean.length; i += 1) next[from + i] = clean[i];
    if (onChange) onChange(next);
    const landed = Math.min(from + clean.length, length - 1);
    const cell = inputRefs.current[landed];
    if (cell) cell.focus();
  }

  function handleChange(index, raw) {
    const digits = raw.replace(/[^0-9]/g, '');

    // More than one digit means the value did not come from a keystroke. Fan it
    // out rather than keeping the last character and throwing the rest away.
    if (digits.length > 1) {
      distribute(index, digits);
      return;
    }

    const digit = digits.slice(-1);
    const next = [...values];
    next[index] = digit;
    if (onChange) onChange(next);
    if (digit && index < length - 1) {
      const nextCell = inputRefs.current[index + 1];
      if (nextCell) nextCell.focus();
    }
  }

  function handleKeyDown(index, event) {
    if (event.key === 'Backspace' && !values[index] && index > 0) {
      const previousCell = inputRefs.current[index - 1];
      if (previousCell) previousCell.focus();
      return;
    }
    // Arrow keys walk the group. Without this the only way back to an earlier
    // cell is to delete every digit after it.
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      const previousCell = inputRefs.current[index - 1];
      if (previousCell) previousCell.focus();
    }
    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      const nextCell = inputRefs.current[index + 1];
      if (nextCell) nextCell.focus();
    }
  }

  function handlePaste(index, event) {
    const pasted = event.clipboardData.getData('text');
    if (!pasted.replace(/[^0-9]/g, '')) return;
    event.preventDefault();
    distribute(index, pasted);
  }

  return (
    <div ref={ref} className={cn('ds-otp', className)} {...rest}>
      {Array.from({ length }).map((unused, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          disabled={disabled}
          value={values[index] || ''}
          placeholder={'•'}
          aria-label={`Digit ${index + 1} of ${length}`}
          // Selecting the existing digit on focus means typing REPLACES it. Without
          // this, typing into an already filled cell produces a two-character input
          // event, which `distribute` then correctly fans forward, so a correction
          // silently overwrites the digits to its right.
          onFocus={(event) => event.target.select()}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          className={cn('ds-otp__cell', activeIndex === index && 'ds-otp__cell--active')}
        />
      ))}
    </div>
  );
});

export default OtpCells;
